import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DocumentType } from 'src/app/core/enums/document-type.enum';
import { IDocumentType } from 'src/app/core/interfaces/common/document/document-type.interface';
import { IDocument } from 'src/app/core/interfaces/common/document/document.interface';
import { IDropdownOption } from 'src/app/core/interfaces/common/ui/dropdown-option.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { DocumentService } from 'src/app/core/services/common/document.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { IEnrollmentStore } from 'src/app/core/store/onboarding/interfaces/enrollment-store.interface';
import { selectAllEnrollment } from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { FileDropZoneComponent } from 'src/app/shared/kendo/dropzones/file-drop-zone/file-drop-zone.component';

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
})
export class DocumentsComponent implements OnInit, OnDestroy {
  homeState: PageState;
  bpState: PageState;
  fpState: PageState;
  cpState: PageState;
  enrollmentId: string;
  options: IDropdownOption[] = [];
  documentTypes: IDocumentType[] = [];
  selectedDocumentType: IDocumentType;
  form: FormGroup;
  uploadSaveUrl = '';
  removeSaveUrl = '';
  documentBehaviorSubject = new BehaviorSubject<IDocument[]>([]);
  subscriptions: Subscription[] = [];
  headerText = 'Add a Document';

  @ViewChild(FileDropZoneComponent) childComponent: FileDropZoneComponent;

  constructor(
    public ds: DocumentService,
    private route: ActivatedRoute,
    private ps: PageStateService,
    private formBuilder: FormBuilder,
    private store: Store<IAppState>
  ) {}

  async ngOnInit(): Promise<void> {
    const props$ = this.store.select(selectAllEnrollment);
    this.subscriptions.push(
      props$.subscribe((props: IEnrollmentStore) => {
        this.homeState = this.ps.getPageStateById(
          props.dashboardStateId.toString()
        );
        this.bpState = this.ps.getPageStateById(
          props.businessProfileStateId.toString()
        );
      })
    );
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        this.documentTypes = data['data'];
        this.options = this.documentTypes
          .filter(
            (documentTypes) =>
              documentTypes.documentTypeName === DocumentType.APAging ||
              documentTypes.documentTypeName ===
                DocumentType.BalanceStatement ||
              documentTypes.documentTypeName === DocumentType.ARAging ||
              documentTypes.documentTypeName === DocumentType.PLStatement ||
              documentTypes.documentTypeName === DocumentType.BankStatement
          )
          .map(
            (value) =>
              ({
                label: value.documentTypeName,
                value: value.documentTypeId,
                action: () => null,
              } as IDropdownOption)
          );
        this.documentBehaviorSubject.next(
          await this.ds.getEnrollmentDocumentsById()
        );
      })
    );

    this.form = this.formBuilder.group({
      filesData: [[], Validators.required],
    });
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  onChange(event: any) {
    // to make error message go when selecting the document;
    this.childComponent.errorMessage = '';
    const selectedDocumentTypeId = parseInt(event.value, 10);
    const selectedDocumentType = this.documentTypes.filter(
      (docType: IDocumentType) =>
        docType.documentTypeId === selectedDocumentTypeId
    );

    this.selectedDocumentType = {
      documentTypeName: selectedDocumentType[0].documentTypeName,
      documentTypeId: selectedDocumentTypeId,
      isCreditSupportingDoc: false,
      isInvoiceSupportingDoc: false,
    } as IDocumentType;
  }

  async onSubmit() {
    const localFiles = this.form.get('filesData')?.value;
    const rawFiles = [];
    for (let i = 0; i < localFiles.length; i += 1) {
      rawFiles.push(localFiles[i].rawFile);
    }

    const enrollmentDocuments: { files: File[]; documentTypeId: number } = {
      files: rawFiles,
      documentTypeId: this.selectedDocumentType.documentTypeId,
    };

    await this.ds.uploadEnrollmentDocument(enrollmentDocuments);
    this.form.get('filesData')?.setValue([]);
    this.documentBehaviorSubject.next(
      await this.ds.getEnrollmentDocumentsById()
    );
  }

  async onDelete(docId: number) {
    const result = this.ds.deleteDocumentById(docId);
    this.documentBehaviorSubject.next(
      await this.ds.getEnrollmentDocumentsById()
    );
    return result;
  }

  async onDownload(docId: number): Promise<IDocument> {
    return this.ds.downloadEnrollmentDocument(docId);
  }
}
